import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, mapTo, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[matTableResponsive]'
})
export class MatTableResponsiveDirective implements AfterViewInit, OnDestroy {
  private onDestroy$ = new Subject<boolean>();

  private thead: HTMLTableSectionElement;
  private tbody: HTMLTableSectionElement;

  private theadChanged$ = new BehaviorSubject(true);
  private tbodyChanged$ = new Subject<boolean>();

  private theadObserver = new MutationObserver(() =>
    this.theadChanged$.next(true)
  );
  private tbodyObserver = new MutationObserver(() =>
    this.tbodyChanged$.next(true)
  );

  constructor(private table: ElementRef, private renderer: Renderer2) {}

  ngAfterViewInit() {
    const checkThead = () => {
      const thead = this.table.nativeElement.querySelector('thead');
      if (thead) {
        this.thead = thead;
        this.tbody = this.table.nativeElement.querySelector('tbody');

        this.theadObserver.observe(this.thead, {
          characterData: true,
          subtree: true
        });

        if (this.tbody) {
          this.tbodyObserver.observe(this.tbody, { childList: true });
        }

        this.startTrackingChanges();
        return true;
      }
      return false;
    };

    // Observe the table element until the thead is avalable
    const tableObserver = new MutationObserver(() => {
      if (checkThead()) {
        tableObserver.disconnect();
      }
    });

    tableObserver.observe(this.table.nativeElement, {
      childList: true,
      subtree: true
    });

    // If it is already present
    checkThead();
  }

  private startTrackingChanges() {
    combineLatest([this.theadChanged$, this.tbodyChanged$])
      .pipe(
        map(() => ({
          headRow: this.thead?.rows?.item(0)!,
          bodyRows: this.tbody?.rows
        })),
        map(({ headRow, bodyRows }) => ({
          columnNames: [...headRow.children].map(
            headerCell => headerCell.textContent!
          ),
          rows: [...bodyRows].map(row => [...row.children])
        })),
        takeUntil(this.onDestroy$)
      )
      .subscribe(({ columnNames, rows }) =>
        rows.forEach(rowCells =>
          rowCells.forEach(cell =>
            this.renderer.setAttribute(
              cell,
              'data-column-name',
              columnNames[(cell as HTMLTableCellElement).cellIndex]
            )
          )
        )
      );
  }

  ngOnDestroy(): void {
    this.theadObserver.disconnect();
    this.tbodyObserver.disconnect();
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }
}
